// ConsultantChat — shared chat surface (header + message list + composer).
// Used both inside the MI desktop right-side drawer (theme="default")
// and the Clara Live mobile /clara/ask route (theme="clara").
//
// The "clara" theme paints navy chrome (header, composer, user
// bubbles, suggestions) and an ivory data surface for assistant
// bubbles — so charts and tables get maximum legibility against the
// dark surrounding chrome (the same pattern Bloomberg terminals use).

import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import {
  Send,
  Loader2,
  Square,
  Sparkles,
  Plus,
  ArrowLeft,
  MapPin,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useConsultant } from "@/contexts/ConsultantContext";
import { MessagePartRenderer } from "./MessagePartRenderer";
import { SUGGESTED_PROMPTS } from "@/data/consultantPrompts";
import { withSuggestionsLast } from "@/lib/consultantStreamParts";
import { answerToText } from "@/lib/consultantCopy";
import { AnswerActions } from "./AnswerActions";
import { ConsultantMemoryMenu } from "./ConsultantMemoryMenu";

// Moved to src/data/consultantPrompts.ts so the standalone app can offer the
// same examples without importing this component. See that file.

// Curated prompt list shown on the Clara Live /clara/ask page (theme=
// "clara"). Aspirational by design — even prompts the consultant can't
// fully answer today educate users on what Clara WILL do, and a graceful
// "I can't pull that yet, but here's what I can tell you…" reply is
// far better UX than a blank empty state.
const CLARA_PROMPTS = [
  "Create a real-time cow-calf newsletter",
  "Create a line chart for boxed beef",
  "Forecast what April feeders may do this week",
  "Tell me about Joplin 6-weight steers",
  "Show the highest price paid for 4-weight steers in Nebraska",
  "Make me a PowerPoint slide about calf scours",
  "Give your outlook on when the border may reopen",
  "Summarize today's feeder market",
  "Show the top 5 barn records set this week",
  "Compare Choice vs. Select boxed beef over 30 days",
];

// Human-readable labels for tool-in-progress indicators. Keeps the UI
// honest about what's happening during the tool execution window (which
// otherwise looks like a generic spinner for 1-3 seconds).
const TOOL_LABELS: Record<string, string> = {
  search_sale_lots: "Searching sale lots",
  get_top_sale_barns: "Pulling top sale barns",
  get_sale_barn_detail: "Loading barn detail",
  get_sale_barn_report_card: "Building barn report card",
  get_feeder_cohort_grid: "Building feeder cohort grid",
  get_cohort_detail: "Loading cohort detail",
  get_cash_feeder_summary: "Summarizing cash feeders",
  get_cash_feeder_history: "Pulling feeder price history",
  get_sell_decision_data: "Loading sell/hold data",
  get_fed_cash_recent_trades: "Pulling fed cash trades",
  get_fed_cash_day_of_week: "Analyzing day-of-week pattern",
  get_packer_margin_history: "Computing packer margin",
  get_packer_margin_snapshot: "Loading packer margin snapshot",
  get_packer_margin_seasonal: "Loading seasonal margin bands",
  get_packer_leverage_context: "Loading packer leverage",
  get_choice_select_spread: "Computing Choice/Select spread",
  get_choice_select_seasonal: "Loading seasonal spread bands",
  get_boxed_beef_primals_snapshot: "Loading boxed beef primals",
  get_market_pulse_verdict: "Reading 5-signal Market Pulse verdict",
  get_feeder_cash_snapshot: "Reading feeder cash snapshot",
  get_slaughter_pace: "Pulling slaughter pace",
  get_slaughter_pace_seasonal: "Loading seasonal kill bands",
  get_slaughter_weekly_history: "Loading weekly kill history",
  get_weight_class_demand: "Analyzing weight class demand",
  get_receipt_weight_mix: "Analyzing receipt weight mix",
  get_weight_slide: "Building weight slide",
  get_52_week_highs: "Pulling 52-week highs",
  get_volume_price_divergence: "Checking volume/price divergence",
  render_boxed_beef_chart: "Building chart",
  render_steer_heifer_chart: "Charting steers vs heifers",
  render_feeder_trend_chart: "Plotting feeder trend",
  emit_suggestions: "Finalizing",
};

function friendlyToolLabel(name: string): string {
  return TOOL_LABELS[name] ?? `Running ${name.replace(/_/g, " ")}`;
}

// Clara brand colors used by the "clara" theme. Mirrors the values
// in src/components/clara/mobile/claraBrand.ts — duplicated here so
// this shared component doesn't take a Clara-only import dependency
// when used by the MI desktop drawer.
const CLARA = {
  navyDeep: "#0A1628",
  navyMid: "#142238",
  amber: "#D4A54A",
  amberBright: "#E9BC5F",
  amberDim: "#8F7032",
  bone: "#F5E8D0",
  dust: "#C4B79A",
  ivory: "#FAFAF5",       // assistant bubble background — data surface
  ink: "#1A1A1A",         // text on ivory
  inkDim: "#6A6A6A",      // muted text on ivory
  ivoryEdge: "#E5E5DF",   // border on ivory
} as const;

type Theme = "default" | "clara";

interface ConsultantChatProps {
  // When provided, renders a back arrow on the left of the header that
  // calls this on tap. Used by the Clara Live route where the user
  // needs an explicit "leave the chat" affordance. The desktop drawer
  // doesn't pass this — it relies on the Sheet's auto X close and
  // clicking outside to dismiss, so its header is unchanged.
  onClose?: () => void;
  // When true (default), the composer textarea takes focus on mount —
  // good on desktop (immediately ready to type) but aggressive on
  // mobile, where it pops the on-screen keyboard before the user has
  // even seen the page. Clara Live passes false.
  autoFocus?: boolean;
  // Visual theme. "default" = MI desktop drawer (unchanged). "clara"
  // = navy chrome with ivory assistant bubbles for the Clara mobile
  // /clara/ask route.
  theme?: Theme;
}

export function ConsultantChat({
  onClose,
  autoFocus = true,
  theme = "default",
}: ConsultantChatProps = {}) {
  const {
    messages,
    isPending,
    runningTool,
    sendMessage,
    stopGeneration,
    regenerate,
    canRegenerate,
    newConversation,
    activeConversationId,
  } = useConsultant();
  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const isClara = theme === "clara";

  // Auto-scroll to bottom whenever messages change
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages.length, isPending]);

  // Focus input on mount (desktop only — mobile would prematurely pop
  // the on-screen keyboard before the user has seen the dialog).
  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  async function handleSend() {
    const text = input.trim();
    if (!text || isPending) return;
    setInput("");
    await sendMessage(text);
    inputRef.current?.focus();
  }

  function handleKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  const isEmpty = messages.length === 0 && !isPending;

  return (
    <div
      className="flex flex-col h-full"
      style={isClara ? { backgroundColor: CLARA.navyDeep } : undefined}
    >
      {/* Header */}
      <div
        className={cn(
          "flex items-center justify-between px-4 py-3 border-b sticky top-0 z-10 print:hidden",
          !isClara && "border-border",
        )}
        style={
          isClara
            ? {
                backgroundColor: CLARA.navyDeep,
                borderBottomColor: CLARA.amberDim,
              }
            : undefined
        }
      >
        <div className="flex items-center gap-2">
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close consultant"
              className={cn(
                "-ml-2 p-2 rounded-md active:scale-95 transition-transform",
                !isClara && "hover:bg-accent",
              )}
              style={isClara ? { color: CLARA.bone } : undefined}
            >
              <ArrowLeft
                className={cn("h-5 w-5", !isClara && "text-foreground")}
              />
            </button>
          )}
          <Sparkles
            className={cn("h-4 w-4", !isClara && "text-primary")}
            style={isClara ? { color: CLARA.amber } : undefined}
          />
          <h2
            className={cn(
              "text-sm font-semibold",
              !isClara && "text-foreground",
            )}
            style={isClara ? { color: CLARA.bone } : undefined}
          >
            RaisedBeef Cattle Consultant
          </h2>
        </div>
        <div className="flex items-center gap-1">
          {/* Personalization & memory controls (Moat Plan A1/A2). Always
              available — lets the user view/clear memory or switch to generic
              answers, on both desktop and Clara mobile. */}
          <ConsultantMemoryMenu isClara={isClara} claraColor={CLARA.bone} />
          {activeConversationId && (
            // When onClose is passed (Clara Live), collapse the "New chat"
            // affordance to an icon-only button so the header (back arrow
            // + Sparkles + "ClaraLive" + this) fits on a 320px iPhone SE
            // without wrapping. Desktop drawer keeps the labeled button —
            // there's plenty of header room there.
            onClose ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={newConversation}
                aria-label="New chat"
                className="h-8 w-8"
                style={isClara ? { color: CLARA.bone } : undefined}
              >
                <Plus className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={newConversation}
                className="text-xs gap-1 h-7"
              >
                <Plus className="h-3 w-3" />
                New chat
              </Button>
            )
          )}
        </div>
      </div>

      {/* Message list */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        {/* ⚠️ A READING COLUMN, NOT THE FULL WINDOW. The thread had no max
            width, so on a wide monitor an answer ran the entire screen — around
            180 characters a line, roughly triple what anyone reads comfortably,
            and the reason long answers felt like work. 46rem lands near 75
            characters at this size. The composer below matches it so the eye
            never has to re-find the left edge. */}
        <div className="mx-auto w-full max-w-[46rem] px-4 py-6 space-y-6">
          {isEmpty ? (
            <EmptyState
              isClara={isClara}
              onSelectPrompt={(p) => {
                setInput(p);
                inputRef.current?.focus();
              }}
            />
          ) : (
            messages.map((msg, i) => (
              <MessageBubble
                key={msg.id}
                message={msg}
                isClara={isClara}
                isLast={i === messages.length - 1}
                onRegenerate={canRegenerate ? regenerate : undefined}
              />
            ))
          )}

          {/* Progress indicator */}
          {isPending &&
            (() => {
              const last = messages[messages.length - 1];
              const streamingMessageHasContent =
                last?.role === "assistant" &&
                last.status === "streaming" &&
                last.content.length > 0;
              if (streamingMessageHasContent) return null;
              if (runningTool) {
                return (
                  <ToolProgressIndicator
                    label={friendlyToolLabel(runningTool)}
                    isClara={isClara}
                  />
                );
              }
              return <TypingIndicator isClara={isClara} />;
            })()}
        </div>
      </div>

      {/* Composer */}
      <div
        className={cn(
          "border-t p-3 sticky bottom-0 print:hidden",
          !isClara && "border-border",
        )}
        style={
          isClara
            ? {
                backgroundColor: CLARA.navyDeep,
                borderTopColor: CLARA.amberDim,
              }
            : undefined
        }
      >
        {/* Same 46rem column as the thread above. Left unmatched, the composer
            starts at the window edge while the answers start well inside it,
            and the eye has to re-find the left margin on every turn. */}
        <div className="mx-auto flex w-full max-w-[46rem] items-end gap-2">
          <Textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Ask about cattle markets, sale barns…"
            disabled={isPending}
            rows={1}
            // text-base (16px) is required to prevent iOS Safari from
            // auto-zooming when the input takes focus — anything <16px
            // triggers the zoom, which makes the page wider than the
            // viewport and forces a horizontal scroll.
            className="min-h-[40px] max-h-[140px] resize-none text-base overflow-y-hidden"
            style={
              isClara
                ? {
                    backgroundColor: CLARA.navyMid,
                    borderColor: CLARA.amberDim,
                    color: CLARA.bone,
                  }
                : undefined
            }
          />
          {/* ⚠️ THE SAME BUTTON STOPS AND SENDS. While an answer is streaming
              it becomes Stop; a separate always-visible stop control is dead
              weight for the 95% of the time nothing is running, and a spinner
              you cannot click is what the composer used to show. */}
          <Button
            onClick={isPending ? stopGeneration : handleSend}
            disabled={!isPending && !input.trim()}
            size="sm"
            className="h-10 px-3"
            aria-label={isPending ? "Stop generating" : "Send"}
            title={isPending ? "Stop generating" : "Send"}
            style={
              isClara && !isPending
                ? {
                    backgroundColor: CLARA.amber,
                    color: CLARA.navyDeep,
                  }
                : undefined
            }
            variant={isPending ? "secondary" : "default"}
          >
            {isPending ? (
              <Square className="h-3.5 w-3.5 fill-current" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
        <p
          className={cn(
            "text-[10px] mt-1.5 px-1",
            !isClara && "text-muted-foreground",
          )}
          style={isClara ? { color: CLARA.dust } : undefined}
        >
          Enter to send · Shift+Enter for newline
        </p>
      </div>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function EmptyState({
  onSelectPrompt,
  isClara,
}: {
  onSelectPrompt: (p: string) => void;
  isClara: boolean;
}) {
  // Clara surface gets the curated 10-prompt list rendered as Clara-
  // brand tiles (matches the home topic-tile aesthetic — pin icon
  // left, label center, chevron right, navy gradient with amber
  // hairline). MI desktop surface (theme="default") uses a spare
  // ChatGPT-style layout — centered heading + horizontal oval pills.
  const prompts = isClara ? CLARA_PROMPTS : SUGGESTED_PROMPTS;

  if (isClara) {
    return (
      <div className="pt-2 pb-6 space-y-3">
        <div className="space-y-1.5 px-1 pt-1">
          {prompts.map((p) => (
            <ClaraPromptTile
              key={p}
              label={p}
              onClick={() => onSelectPrompt(p)}
            />
          ))}
        </div>
      </div>
    );
  }

  // MI desktop / page surface — centered heading + oval pill chips.
  return (
    <div className="py-10 flex flex-col items-center justify-center">
      <h3 className="text-lg md:text-xl font-semibold text-foreground mb-5 text-center">
        Ready when you are.
      </h3>
      <div className="flex flex-wrap items-center justify-center gap-2 max-w-xl">
        {prompts.map((p) => (
          <button
            key={p}
            onClick={() => onSelectPrompt(p)}
            className="text-xs px-4 py-2 rounded-full border border-border bg-background hover:bg-accent hover:text-accent-foreground transition-colors"
          >
            {p}
          </button>
        ))}
      </div>
    </div>
  );
}

// Curated prompt tile for Clara mode. Visual target matches the
// home-screen NavTile (navy radial gradient, amber hairline, inset
// sheen) but adds a leading pin icon and uses sentence-case text
// instead of all-caps labels — so the prompts read as suggestions
// rather than navigation destinations.
function ClaraPromptTile({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group w-full flex items-center gap-2.5 px-3 py-2.5 rounded transition-all active:scale-[0.99] hover:brightness-110 text-left"
      style={{
        backgroundImage: `radial-gradient(ellipse at center top, ${CLARA.navyMid} 0%, ${CLARA.navyDeep} 100%)`,
        backgroundColor: CLARA.navyDeep,
        border: `1px solid rgba(212, 165, 74, 0.45)`,
        boxShadow: [
          "inset 0 1px 0 rgba(212, 165, 74, 0.12)",
          "inset 0 -1px 0 rgba(0, 0, 0, 0.5)",
          "0 1px 2px rgba(0, 0, 0, 0.4)",
        ].join(", "),
      }}
    >
      <MapPin
        className="h-4 w-4 flex-shrink-0"
        style={{ color: CLARA.amber }}
        strokeWidth={2}
      />
      <span
        className="flex-1 text-[13px] leading-tight"
        style={{
          color: CLARA.bone,
          fontFamily: "'Roboto', system-ui, sans-serif",
        }}
      >
        {label}
      </span>
      <ChevronRight
        className="h-3.5 w-3.5 flex-shrink-0 transition-transform group-active:translate-x-0.5"
        style={{ color: CLARA.dust }}
        strokeWidth={2}
      />
    </button>
  );
}

function MessageBubble({
  message,
  isClara,
  isLast = false,
  onRegenerate,
}: {
  message: ReturnType<typeof useConsultant>["messages"][number];
  isClara: boolean;
  /** Only the newest answer offers "Ask again" — re-running an older turn
   *  would append its answer to the bottom, far from the question it belongs
   *  to, which reads as the consultant answering something nobody asked. */
  isLast?: boolean;
  onRegenerate?: () => void;
}) {
  const isUser = message.role === "user";
  const isError = message.status === "error";
  const isStreaming = message.status === "streaming";
  // ⚠️ The model sometimes calls emit_suggestions partway through and keeps
  // writing, which renders the chips mid-answer. Ordering is fixed here
  // rather than by asking the prompt more firmly — see withSuggestionsLast.
  const orderedParts = withSuggestionsLast(message.content);
  const lastIdx = orderedParts.length - 1;

  // Clara theme: user = navy with amber border, assistant = ivory
  // (data surface). Default theme: shadcn semantic tokens unchanged.
  const claraStyle = isClara
    ? isError
      ? { backgroundColor: "rgba(184, 92, 42, 0.15)", borderColor: "rgba(184, 92, 42, 0.5)" }
      : isUser
        ? { backgroundColor: CLARA.navyMid, borderColor: CLARA.amberDim, color: CLARA.bone }
        : { backgroundColor: CLARA.ivory, borderColor: CLARA.ivoryEdge, color: CLARA.ink }
    : undefined;

  // ⚠️ OFFERED ONLY WHEN THERE IS SOMETHING TO COPY. answerToText returns
  // empty for a message that is only follow-up chips or whitespace, and a copy
  // button that silently puts nothing on the clipboard is worse than none.
  const copyText = !isUser && !isStreaming ? answerToText(message.content) : "";

  // ⚠️ ONLY THE QUESTION IS A BUBBLE. Both roles used to sit in bordered boxes
  // capped at 92% width, which is chat-app furniture — and this is not chat, it
  // is a cited analysis somebody reads and pastes into an email. The answer now
  // sits plainly on the page with nothing around it, so the eye goes to the
  // words and the tables can use the full column. The question stays boxed
  // because a thread needs to show at a glance who said what.
  return (
    <div
      className={cn("flex", isUser ? "justify-end" : "justify-start")}
      data-consultant-bubble={isUser ? "user" : "assistant"}
    >
      <div
        className={cn(
          "space-y-2",
          isUser
            ? "max-w-[85%] rounded-2xl rounded-br-md border px-3.5 py-2"
            : "w-full",
          !isClara && isUser && "bg-primary/10 border-primary/20",
          !isClara && isError && "rounded-lg border px-3 py-2 bg-destructive/10 border-destructive/30",
        )}
        style={isUser || isError ? claraStyle : isClara ? { color: CLARA.ink } : undefined}
      >
        {orderedParts.map((part, i) => (
          <div key={i} className="relative">
            <MessagePartRenderer
              part={part}
              surface={isClara && !isUser ? "light" : "dark"}
              messageId={message.id}
            />
            {isStreaming &&
              i === lastIdx &&
              part.type === "text" && (
                <span
                  className={cn(
                    "inline-block w-1.5 h-4 ml-0.5 animate-pulse align-middle",
                    !isClara && "bg-primary/70",
                  )}
                  style={
                    isClara
                      ? {
                          backgroundColor: isUser
                            ? CLARA.amber
                            : CLARA.ink,
                        }
                      : undefined
                  }
                />
              )}
          </div>
        ))}

        {copyText ? (
          <AnswerActions
            className="mt-0.5"
            messageId={message.id}
            parts={message.content}
            onRegenerate={isLast ? onRegenerate : undefined}
            colour={isClara ? CLARA.ink : undefined}
          />
        ) : null}
      </div>
    </div>
  );
}

function ToolProgressIndicator({
  label,
  isClara,
}: {
  label: string;
  isClara: boolean;
}) {
  return (
    <div className="flex justify-start">
      <div
        className={cn(
          "max-w-[92%] rounded-lg px-3 py-2 border",
          !isClara && "bg-primary/5 border-primary/20",
        )}
        style={
          isClara
            ? {
                backgroundColor: CLARA.navyMid,
                borderColor: CLARA.amberDim,
              }
            : undefined
        }
      >
        <div
          className={cn(
            "flex items-center gap-2 text-xs",
            !isClara && "text-foreground",
          )}
          style={isClara ? { color: CLARA.bone } : undefined}
        >
          <Loader2
            className={cn("h-3 w-3 animate-spin", !isClara && "text-primary")}
            style={isClara ? { color: CLARA.amber } : undefined}
          />
          <span>{label}…</span>
        </div>
      </div>
    </div>
  );
}

function TypingIndicator({ isClara }: { isClara: boolean }) {
  return (
    <div className="flex justify-start">
      <div
        className={cn(
          "max-w-[92%] rounded-lg px-3 py-2 border",
          !isClara && "bg-muted/20 border-border",
        )}
        style={
          isClara
            ? {
                backgroundColor: CLARA.navyMid,
                borderColor: CLARA.amberDim,
              }
            : undefined
        }
      >
        <div
          className={cn(
            "flex items-center gap-2 text-xs",
            !isClara && "text-muted-foreground",
          )}
          style={isClara ? { color: CLARA.dust } : undefined}
        >
          <Loader2
            className="h-3 w-3 animate-spin"
            style={isClara ? { color: CLARA.amber } : undefined}
          />
          <span>Analyzing the data…</span>
        </div>
      </div>
    </div>
  );
}
